import React, { useEffect, useState } from 'react'
import { Motif } from '../../types/motifs'
import { useContextSelectedMotifs } from '../../contexts/selectedMotifContext'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import { isMobile } from 'react-device-detect'

const StyledButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.black70};
  border-radius: 12px;
  display: flex;
  align-items: justify-content;
  justify-content: justify-content;
  padding: 0;
  grid-row: 2 / 2;
  grid-column: 1 / 1;
  align-self: start;

  &:disabled {
    cursor: not-allowed;
    .circle {
      opacity: 0.3;
    }
  }
`

const SelectorButton = styled(StyledButton)`
  gap: 5px;
  position: relative;
  left: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  height: 90px;

  p {
    text-align: right;
    margin: 0;
  }

  .selector-text-container {
    font-family: ${({ theme }) => theme.font.semiBold};
    font-weight: 400;
    font-size: 18px;
    line-height: 30px;
    letter-spacing: 0%;
    text-align: left;
    margin-left: 1rem;
  }
  .selector-text {
    color: ${({ theme }) => theme.colors.red};
    margin-right: -1rem;
  }

  .selector-numbered-text {
    .mobile {
      flex-direction: row-reverse;
      gap: 9px;

      p {
        font-size: 13px !important;
        text-align: left;
      }
    }
  }
  .circle {
    min-height: 44px;
    min-width: 44px;
    border-radius: 50%;
    justify-content: center;
    align-items: center;
    display: flex;

    svg {
      width: 45%;
      height: 45%;
    }

    &.add {
      background-color: ${({ theme }) => theme.colors.red};

      svg {
        color: ${({ theme }) => theme.colors.white};
      }
    }

    &.remove {
      background-color: transparent;
      border: 1px solid black;

      svg {
        color: ${({ theme }) => theme.colors.red};
      }
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    p {
      margin-left: 10px;
      font-size: 15px;
      line-height: 24px;
      text-align: left;
    }

    .circle {
      min-height: 70px;
      min-width: 70px;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.md}) {
    .circle {
      min-height: 80px;
      min-width: 80px;
      }
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xl}) {
    .circle {
      min-height: 90px;
      min-width: 90px;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
    .circle {
      min-height: 112px;
      min-width: 112px;
    }
  }

  @media (min-height: ${({ theme }) => theme.breakpoints.deviceH.md}) {
    p {
      font-size: 15px;
    }
  }
`

interface Props {
  motif: Motif
}

const MotifSelector = ({ motif }: Props) => {
  const { t } = useTranslation()
  const { selectedMotifs, isSelected, selectMotif, deselectMotif } = useContextSelectedMotifs()
  const [numberedText, setNumberedText] = useState<string | false>(false)
  const [isMotifSelected, setIsMotifSelected] = useState<boolean>(false)

  useEffect(() => {
    setIsMotifSelected(isSelected(motif.id))
  }, [selectedMotifs.length])

  useEffect(() => {
    switch (selectedMotifs.length) {
      case 0:
        setNumberedText('maxFourMotifs')
        break
      case 1:
        setNumberedText('threeMoreMotifs')
        break
      case 2:
        setNumberedText('twoMoreMotifs')
        break
      case 3:
        setNumberedText('oneMoreMotif')
        break
      case 4:
        setNumberedText(false)
        break
    }
  }, [selectedMotifs.length])

  return (
    <div className="selectorButtonWrapper">
      <SelectorButton
        className={`${isMobile ? 'mobile' : ''}`}
        onClick={event => {
          event.stopPropagation()
          if (isMotifSelected) {
            deselectMotif(motif.id)
          } else {
            selectMotif(motif)
          }
        }}
        disabled={selectedMotifs.length === 4 && !isMotifSelected}
      >
        <div className={`circle ${isMotifSelected ? 'remove' : 'add'}`}>
          {isMotifSelected ? <SVG src="/icons/minus.svg" /> : <SVG src="/icons/plus.svg" />}
        </div>
        <div className="selector-text-container">
          <div className="selector-text">
            {selectedMotifs.length === 4
              ? t('maxMotifsReached')
                  .split(';')
                  .map((part, index, array) => (
                    <React.Fragment key={index}>
                      {part}
                      {index < array.length - 1 && <br />}
                    </React.Fragment>
                  ))
              : isMotifSelected
              ? t('removeThisMotif')
              : t('selectThisMotif')}
          </div>
          {numberedText && <div className="selector-numbered-text">{t(numberedText)}</div>}
        </div>
      </SelectorButton>
    </div>
  )
}

export default MotifSelector
